import {
  ACCESS_LEVEL_LABELS,
  REVIEW_FREQUENCY_LABELS,
  getCollection,
  type AccessReviewStatus,
  type AssignmentAccessLevel,
  type RecordAssignment,
} from '@grc/types';
import { ApiRequestError } from '../../lib/api.js';
import { Badge, formatDate, type BadgeTone } from '../../components/ui.js';

/**
 * Helpers shared by the access-review pages, the assignments dialog, the
 * "My Software Access" page and the dashboard card (spec §5).
 */

export const REVIEW_STATUS_TONE: Record<AccessReviewStatus, BadgeTone> = {
  in_progress: 'info',
  completed: 'primary',
  approved: 'success',
};

/**
 * Toast text for a failed request: the first per-field issue when the server
 * sent one (400 validation_failed), else its message, else the fallback.
 */
export function apiErrorMessage(err: unknown, fallback: string): string {
  if (!(err instanceof ApiRequestError)) return fallback;
  const issues = err.details?.issues as Array<{ message?: string }> | undefined;
  return issues?.[0]?.message ?? err.message;
}

/** Access-review pages and the dashboard card share this query-key prefix. */
export function reviewsKey(orgId: string): readonly ['access-reviews', string] {
  return ['access-reviews', orgId];
}

/** Local calendar date as YYYY-MM-DD (the API's date-only format). */
export function localDateIso(date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** YYYY-MM-DD plus N months, clamped to the end of the target month (mirrors the worker). */
export function addMonthsIso(from: string, months: number): string {
  const [y, m, d] = from.split('-').map(Number);
  const target = new Date(y, m - 1 + months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(d, lastDay));
  return localDateIso(target);
}

/** YYYY-MM-DD plus N days. */
export function addDaysIso(from: string, days: number): string {
  const [y, m, d] = from.split('-').map(Number);
  return localDateIso(new Date(y, m - 1, d + days));
}

/** Default name for a new review, e.g. "Q3 2026 access review". */
export function defaultReviewName(date = new Date()): string {
  return `Q${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear()} access review`;
}

export function accessLevelLabel(level: AssignmentAccessLevel): string {
  return level ? ACCESS_LEVEL_LABELS[level] : 'Not recorded';
}

/** "All software", "GitLab", "AWS · privileged grants only"… */
export function scopeLabel(review: {
  collection: string;
  recordId: string | null;
  recordName: string | null;
  privilegedOnly: boolean;
}): string {
  const collection = getCollection(review.collection)?.name ?? review.collection;
  const target = review.recordId
    ? (review.recordName ?? '(record deleted)')
    : `All ${collection.toLowerCase()}`;
  return review.privilegedOnly ? `${target} · privileged grants only` : target;
}

/** A grant's next re-certification date: red when overdue, "No review" when never reviewed. */
export function NextReview({
  assignment: a,
}: {
  assignment: Pick<RecordAssignment, 'nextReviewOn' | 'overdue' | 'reviewFrequency'>;
}) {
  if (!a.nextReviewOn) return <>{a.reviewFrequency === 'none' ? 'No review' : '—'}</>;
  if (a.overdue) {
    return <Badge tone="danger">Overdue · {formatDate(a.nextReviewOn)}</Badge>;
  }
  return (
    <>
      {formatDate(a.nextReviewOn)}
      <span className="cell-muted">{REVIEW_FREQUENCY_LABELS[a.reviewFrequency]}</span>
    </>
  );
}
